import React, { useState } from 'react';
import {  X, Send, Key , Loader2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import Dropdown from '../../components/Dropdown';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const TakeDeliveryActionModal = ({ delivery, onClose, onSuccess }) => {
  const [actionOutcome, setActionOutcome] = useState('Delivered');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [scheduledDate, setScheduledDate] = useState(delivery?.scheduledDate ? new Date(delivery.scheduledDate) : null);
  const [notes, setNotes] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(!!delivery?.verificationCode);
  const [loading, setLoading] = useState(false);

  const [actionOptions, setActionOptions] = useState([]);

  React.useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await api.get('/masters/delivery-statuses');
        const rawStatuses = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const activeStatuses = rawStatuses
          .filter(s => s.status === 'Active')
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (activeStatuses.length > 0) {
          const mapped = activeStatuses.map(s => ({
            value: s.name,
            label: s.name
          }));
          setActionOptions(mapped);
          setActionOutcome(mapped[0].value);
        } else {
          setActionOptions([
            { value: 'Pending', label: 'Pending' },
            { value: 'Delivered', label: 'Delivered' },
            { value: 'Cancelled', label: 'Cancelled' }
          ]);
        }
      } catch (err) {
        console.error("Failed to load delivery statuses master", err);
      }
    };
    fetchStatuses();
  }, []);

  const isDelivered = actionOutcome?.toLowerCase().includes('delivered');
  const isReschedule = actionOutcome?.toLowerCase().includes('reschedule');
  const customerEmail = delivery.partyEmail || delivery.lead?.email;

  const handleSendOtp = async () => {
    if (!customerEmail) {
      return toast.error("No customer email address specified for this delivery. Please edit delivery to add party email.");
    }
    try {
      setSendingOtp(true);
      const res = await api.post(`/deliveries/${delivery.id}/send-otp`);
      toast.success(res.data?.message || `Verification code sent to ${customerEmail}`);
      setOtpSent(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send verification code");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
        if (!actionOutcome) return toast.error("Please select an action outcome");
    if (isReschedule && !scheduledDate) return toast.error("Please select a new date for rescheduling");
    if (isDelivered && !paymentMethod) {
      return toast.error("Please select a Payment Method.");
    }
    if (isDelivered && !otpCode.trim()) {
      return toast.error("Please enter the 6-digit verification code received by the customer.");
    }

    try {
      setLoading(true);
      
      const payload = {
        status: actionOutcome,
        details: notes || paymentMethod ? `${delivery.details || ''}\n\n[${actionOutcome}] ${paymentMethod ? 'Payment: ' + paymentMethod : ''}${notes ? '\nNotes: ' + notes : ''}`.trim() : delivery.details,
        actionTitle: `Action taken: ${actionOutcome}`, paymentMethod: paymentMethod,
        actionNotes: notes || null,
        otpCode: otpCode.trim()
      };
      
      if (isReschedule || scheduledDate) {
        payload.scheduledDate = scheduledDate.toISOString();
      }

      await api.put(`/deliveries/${delivery.id}`, payload);

      toast.success("Delivery completed and verified successfully!");
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = loading || (isDelivered && otpCode.trim().length !== 6) || (isReschedule && !scheduledDate);

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>Take Action</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#4b5563', fontSize: '0.9rem' }}>Action Outcome</label>
            <Dropdown
              value={actionOutcome}
              onChange={(val) => setActionOutcome(val)}
              options={actionOptions}
              style={{ border: '1px solid var(--color-border)', borderRadius: '8px' }}
            />
          </div>

          {/* OTP Verification Section for Delivered Status */}
          {isDelivered && (
            <div style={{ marginBottom: '1.5rem', padding: '1.25rem', backgroundColor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0369a1', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Key size={18} color="#0284c7" /> Customer Email Verification Code <span style={{ color: 'var(--color-danger)' }}>*</span>
              </div>
              
              <p style={{ margin: '0 0 0.85rem 0', fontSize: '0.825rem', color: '#475569' }}>
                Send the 6-digit verification code to customer email address and enter it below to complete delivery.
              </p>

              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', backgroundColor: 'white', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
                <div style={{ fontSize: '0.85rem', color: '#0f172a' }}>
                  <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', fontWeight: '500' }}>CUSTOMER EMAIL</span>
                  <strong>{customerEmail || 'No Email Address Specified'}</strong>
                </div>

                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !customerEmail}
                  className="btn"
                  style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}
                >
                  <Send size={14} /> {sendingOtp ? 'Sending Code...' : otpSent ? 'Resend Code' : 'Send Verification Code'}
                </button>
              </div>

              
            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '600', color: '#0369a1', fontSize: '0.85rem' }}>
                Payment Method <span style={{color: 'var(--color-danger)'}}>*</span>
              </label>
              <Dropdown
                value={paymentMethod}
                onChange={(val) => setPaymentMethod(val)}
                placeholder="Select Payment Method..."
                options={[
                  { value: 'UPI', label: 'UPI' },
                  { value: 'Cash', label: 'Cash' },
                  { value: 'Bank Transfer', label: 'Bank Transfer' },
                  { value: 'Advance Paid', label: 'Advance Paid' }
                ]}
                style={{ border: '1px solid var(--color-border)', borderRadius: '8px' }}
              />
            </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '600', color: '#0369a1', fontSize: '0.85rem' }}>
                  Enter 6-Digit OTP Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="E.g. 584920"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid #0284c7', borderRadius: '8px', fontSize: '1.25rem', fontWeight: '800', letterSpacing: '4px', textAlign: 'center', backgroundColor: 'white' }}
                />
              </div>
            </div>
          )}

          {isReschedule && (
            <div className="form-group" style={{ marginBottom: '1.5rem', padding: '1.25rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#0369a1', fontSize: '0.9rem' }}>New Scheduled Date & Time <span style={{color: 'var(--color-danger)'}}>*</span></label>
              <DatePicker
                selected={scheduledDate}
                onChange={(date) => setScheduledDate(date)}
                className="input-field"
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="dd/MM/yyyy h:mm aa"
                placeholderText="DD/MM/YYYY --:--"
                minDate={new Date()}
                style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: 'white' }}
                wrapperClassName="w-full"
              />
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#4b5563', fontSize: '0.9rem' }}>Notes (Optional)</label>
            <textarea 
              value={notes} 
              onChange={(e) => setNotes(e.target.value)} 
              className="input-field" 
              rows="4" 
              style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '8px', resize: 'vertical' }} 
              placeholder="Summary of interaction or reason for reschedule..."
            ></textarea>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.6rem 1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: '600', color: '#111827' }}>Cancel</button>
            <button 
              type="submit" 
              disabled={isSubmitDisabled} 
              className="btn btn-primary" 
              style={{ 
                padding: '0.6rem 1.5rem', 
                border: 'none', 
                borderRadius: '8px', 
                background: isSubmitDisabled ? '#9ca3af' : '#10b981', 
                color: 'white', 
                cursor: isSubmitDisabled ? 'not-allowed' : 'pointer', 
                fontWeight: '600',
                opacity: isSubmitDisabled ? 0.7 : 1 
              }}
            >
              {loading && <Loader2 size={16} className="spinner-icon" />}
              {loading ? 'Saving...' : 'Save Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TakeDeliveryActionModal;
